import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { userAPI, progressAPI } from '../../api/client'
import AppLayout from '../../components/layout/AppLayout'

const H = ({ style, ...p }: React.HTMLAttributes<HTMLElement>) => (
  <span style={{ fontFamily: "'Space Grotesk', sans-serif", ...style }} {...p} />
)

const CEFR_LEVELS = ['A2', 'B1', 'B2', 'C1']
const IELTS_BANDS = ['4.5', '5.5', '6.5', '7.5']
const EXAM_OPTIONS: { code: 'vstep' | 'ielts_academic' | 'ielts_general'; label: string }[] = [
  { code: 'vstep', label: 'VSTEP' },
  { code: 'ielts_academic', label: 'IELTS Academic' },
  { code: 'ielts_general', label: 'IELTS General' },
]

/** Student Profile & Settings */
export default function ProfilePage() {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { data: dashboard } = useQuery({ queryKey: ['progress-dashboard'], queryFn: progressAPI.dashboard })
  const { data: userMe } = useQuery({ queryKey: ['user-me'], queryFn: userAPI.me })

  const [name, setName] = useState('')
  const [examType, setExamType] = useState<'vstep' | 'ielts_academic' | 'ielts_general'>('vstep')
  const [targetLevel, setTargetLevel] = useState<string>('')
  const [examDate, setExamDate] = useState('')
  const [dailyGoal, setDailyGoal] = useState(45)
  const [motivation, setMotivation] = useState('')
  const [notifReminder, setNotifReminder] = useState(true)
  const [notifWeekly, setNotifWeekly] = useState(true)
  const [saveSuccess, setSaveSuccess] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Pre-fill form from loaded user data
  useEffect(() => {
    if (userMe) {
      setName((userMe as any).name ?? '')
      setExamType((userMe as any).examType ?? 'vstep')
      setTargetLevel((userMe as any).targetLevel ?? '')
      setDailyGoal((userMe as any).dailyStudyMinutes ?? 45)
      setMotivation((userMe as any).motivation ?? '')
      setNotifReminder((userMe as any).notifDailyReminder ?? true)
      setNotifWeekly((userMe as any).notifWeeklyReport ?? true)
      // examDate from DB is ISO string or null
      const ed = (userMe as any).examDate
      if (ed) setExamDate(ed.slice(0, 10))
    }
  }, [userMe])

  const saveMutation = useMutation({
    mutationFn: () => userAPI.updateProfile({
      name: name || undefined,
      examType,
      targetLevel: targetLevel || undefined,
      dailyStudyMinutes: dailyGoal,
      examDate: examDate || null,
      motivation: motivation,
      notifDailyReminder: notifReminder,
      notifWeeklyReport: notifWeekly,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-me'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      setSaveError(null)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 2500)
    },
    onError: (err: any) => {
      setSaveSuccess(false)
      setSaveError(err?.message ?? 'Lưu thất bại. Vui lòng thử lại.')
    },
  })

  // ── Auto-save for the goal section (exam type / target level / exam date / daily goal).
  // Changing any control persists immediately; the slider is debounced.
  const [goalSaved, setGoalSaved] = useState(false)
  const goalSaveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const goalsMutation = useMutation({
    mutationFn: (body: Parameters<typeof userAPI.updateProfile>[0]) => userAPI.updateProfile(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-me'] })
      qc.invalidateQueries({ queryKey: ['dashboard'] })
      setSaveError(null)
      setGoalSaved(true)
      setTimeout(() => setGoalSaved(false), 1800)
    },
    onError: (err: any) => {
      setGoalSaved(false)
      setSaveError(err?.message ?? 'Lưu thất bại. Vui lòng thử lại.')
    },
  })

  // Persist goal fields. Pass overrides for the value just changed (state setters are async,
  // so the new value isn't in `examType`/`targetLevel`/… yet at call time).
  const saveGoals = (override: {
    examType?: 'vstep' | 'ielts_academic' | 'ielts_general'
    targetLevel?: string
    dailyStudyMinutes?: number
    examDate?: string | null
  } = {}) => {
    goalsMutation.mutate({
      examType: override.examType ?? examType,
      targetLevel: ('targetLevel' in override ? override.targetLevel : targetLevel) || undefined,
      dailyStudyMinutes: override.dailyStudyMinutes ?? dailyGoal,
      examDate: ('examDate' in override ? override.examDate : examDate) || null,
    })
  }

  // Debounced save for the daily-goal slider (fires many onChange events while dragging).
  const saveDailyGoalDebounced = (minutes: number) => {
    clearTimeout(goalSaveTimer.current)
    goalSaveTimer.current = setTimeout(() => saveGoals({ dailyStudyMinutes: minutes }), 600)
  }

  // Derive current CEFR level from dashboard data (forecastLevel or skill avg).
  // Show null when user hasn't taken the diagnostic — UI will render "Chưa xác định".
  const learningProfile = (dashboard as any)?.learningProfile
  const skillValues: number[] = learningProfile?.skills
    ? (Object.values(learningProfile.skills) as number[]).filter(Boolean)
    : []
  const avgSkill = skillValues.length > 0 ? skillValues.reduce((a, b) => a + b, 0) / skillValues.length : 0
  const derivedFromScores = avgSkill >= 9 ? 'C1'
    : avgSkill >= 7.5 ? 'B2'
    : avgSkill >= 5 ? 'B1'
    : avgSkill >= 2 ? 'A2'
    : null
  const currentLevel: string | null = learningProfile?.forecastLevel ?? derivedFromScores
  const email = (userMe as any)?.email ?? '—'
  const subscriptionTier = (userMe as any)?.subscriptionTier ?? 'FREE'

  return (
    <AppLayout>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
          <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1.125rem', fontVariationSettings: "'FILL' 1" }}>settings</span>
          <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#004be2' }}>Cài đặt</span>
        </div>
        <H style={{ display: 'block', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', fontWeight: 900, letterSpacing: '-0.03em', marginBottom: '0.5rem' }}>
          Hồ sơ <span style={{ color: '#004be2', fontStyle: 'italic' }}>học viên</span>
        </H>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(16rem, 1fr))', gap: '1.5rem', alignItems: 'start' }}>
        {/* Left column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Profile card */}
          <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', textAlign: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
            <div style={{ position: 'relative', display: 'inline-block', marginBottom: '1.25rem' }}>
              <div style={{ width: '7rem', height: '7rem', borderRadius: '50%', background: 'linear-gradient(135deg,#d1dcff,#809bff)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '4px solid #f6f6ff', boxShadow: '0 8px 24px rgba(0,75,226,0.15)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '3.5rem', color: '#004be2', fontVariationSettings: "'FILL' 1" }}>account_circle</span>
              </div>
              <button style={{ position: 'absolute', bottom: '0', right: '0', width: '2rem', height: '2rem', borderRadius: '50%', background: '#004be2', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,75,226,0.3)' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '1rem', color: 'white' }}>edit</span>
              </button>
            </div>
            <H style={{ display: 'block', fontWeight: 800, fontSize: '1.375rem', marginBottom: '0.25rem' }}>{(userMe as any)?.name ?? 'Học viên EngHustle'}</H>
            <p style={{ color: '#535b71', fontSize: '0.875rem', marginBottom: '1.25rem' }}>{currentLevel ?? 'Chưa xác định'} · {subscriptionTier === 'PRO' || subscriptionTier === 'premium' ? 'Pro' : 'Free'}</p>
            <div style={{ width: '100%', height: '1px', background: '#eef0ff', marginBottom: '1.25rem' }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', textAlign: 'left' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                <span style={{ color: '#535b71' }}>Email</span>
                <span style={{ fontWeight: 600, color: '#272e42', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '60%' }}>{email}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem' }}>
                <span style={{ color: '#535b71' }}>Trình độ hiện tại</span>
                {currentLevel ? (
                  <span style={{ fontWeight: 700, color: '#004be2' }}>{currentLevel}</span>
                ) : (
                  <a href="/onboarding" style={{ fontWeight: 700, color: '#8e00d5', textDecoration: 'none', fontSize: '0.8125rem' }}>Làm bài đánh giá →</a>
                )}
              </div>
            </div>
          </div>

          {/* Exam goal card (FR-029) */}
          <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '1.75rem', borderLeft: '4px solid #004be2' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', marginBottom: '1.25rem' }}>
              <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1.25rem' }}>target</span>
              <H style={{ fontWeight: 800, fontSize: '1rem' }}>Mục tiêu {examType === 'vstep' ? 'VSTEP' : 'IELTS'}</H>
            </div>

            {/* Exam type selector */}
            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Kỳ thi</label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
              {EXAM_OPTIONS.map(ex => (
                <button
                  key={ex.code}
                  // Changing exam resets target so the band/level stays valid; auto-save the exam type
                  onClick={() => { setExamType(ex.code); setTargetLevel(''); saveGoals({ examType: ex.code, targetLevel: '' }) }}
                  style={{
                    flex: '1 1 auto', padding: '0.625rem 0.5rem', borderRadius: '0.75rem', border: 'none', cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.8125rem',
                    background: examType === ex.code ? '#004be2' : 'white',
                    color: examType === ex.code ? 'white' : '#535b71',
                    boxShadow: examType === ex.code ? '0 4px 16px rgba(0,75,226,0.25)' : 'none',
                    transition: 'all 0.2s',
                  }}
                >{ex.label}</button>
              ))}
            </div>

            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>{examType === 'vstep' ? 'Cấp độ mục tiêu' : 'Band mục tiêu'}</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', marginBottom: '1.5rem' }}>
              {(examType === 'vstep' ? CEFR_LEVELS : IELTS_BANDS).map(lvl => (
                <button
                  key={lvl}
                  onClick={() => { setTargetLevel(lvl); saveGoals({ targetLevel: lvl }) }}
                  style={{
                    padding: '0.75rem 0.25rem', borderRadius: '0.75rem', border: 'none', cursor: 'pointer', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: '0.875rem',
                    background: targetLevel === lvl ? '#004be2' : 'white',
                    color: targetLevel === lvl ? 'white' : '#535b71',
                    boxShadow: targetLevel === lvl ? '0 4px 16px rgba(0,75,226,0.25)' : 'none',
                    transition: 'all 0.2s',
                  }}
                >{lvl}</button>
              ))}
            </div>
            {/* Exam date picker */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>
                Ngày thi dự kiến
              </label>
              <input
                type="date"
                value={examDate}
                onChange={e => { setExamDate(e.target.value); saveGoals({ examDate: e.target.value || null }) }}
                style={{
                  width: '100%', padding: '0.75rem 1rem', borderRadius: '0.875rem',
                  background: 'white', border: 'none', outline: 'none',
                  fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', color: '#272e42',
                  boxSizing: 'border-box', cursor: 'pointer',
                }}
              />
              {examDate && (
                <button
                  onClick={() => { setExamDate(''); saveGoals({ examDate: null }) }}
                  style={{ marginTop: '0.375rem', background: 'none', border: 'none', cursor: 'pointer', color: '#a5adc6', fontSize: '0.75rem', fontWeight: 600 }}
                >
                  × Xóa ngày thi
                </button>
              )}
            </div>
            {examDate && (
              <div style={{ background: 'rgba(255,255,255,0.6)', borderRadius: '1rem', padding: '1rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.625rem' }}>
                  <span style={{ fontSize: '0.6875rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#535b71' }}>Đếm ngược kỳ thi</span>
                  <span style={{ fontWeight: 900, color: '#004be2', fontSize: '0.9375rem' }}>
                    {Math.max(0, Math.ceil((new Date(examDate).getTime() - Date.now()) / 86400000))} ngày
                  </span>
                </div>
                <div style={{ height: '0.5rem', background: '#d1dcff', borderRadius: '9999px', overflow: 'hidden', marginBottom: '0.625rem' }}>
                  <div style={{
                    height: '100%',
                    width: `${Math.min(100, Math.max(5, 100 - (Math.max(0, Math.ceil((new Date(examDate).getTime() - Date.now()) / 86400000)) / 365) * 100))}%`,
                    background: '#004be2', borderRadius: '9999px',
                  }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem', color: '#535b71' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>event</span>
                  {new Date(examDate).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                </div>
              </div>
            )}

            {/* Auto-save status — goal fields persist on change, no button needed */}
            <div style={{ marginTop: '1rem', minHeight: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.375rem', fontSize: '0.8125rem', fontWeight: 600 }}>
              {goalsMutation.isPending ? (
                <span style={{ color: '#535b71', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>sync</span>
                  Đang lưu...
                </span>
              ) : goalSaved ? (
                <span style={{ color: '#006571', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                  Đã lưu tự động
                </span>
              ) : saveError ? (
                <span style={{ color: '#d92d20', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>error</span>
                  {saveError}
                </span>
              ) : (
                <span style={{ color: '#a5adc6' }}>Thay đổi được lưu tự động</span>
              )}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          {/* Subscription + Daily goal */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(14rem, 1fr))', gap: '1.25rem' }}>
            {/* Subscription */}
            <div style={{ background: 'rgba(255,255,255,0.65)', backdropFilter: 'blur(24px)', borderRadius: '1.5rem', padding: '1.75rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <span style={{ background: '#d1dcff', color: '#004be2', fontSize: '0.625rem', fontWeight: 800, padding: '0.25rem 0.75rem', borderRadius: '9999px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Gói hiện tại</span>
                  <span className="material-symbols-outlined" style={{ color: '#8e00d5', fontVariationSettings: "'FILL' 1", fontSize: '1.25rem' }}>verified</span>
                </div>
                <H style={{ display: 'block', fontWeight: 900, fontSize: '1.75rem', color: '#004be2', fontStyle: 'italic', marginBottom: '0.375rem' }}>{subscriptionTier === 'PRO' || subscriptionTier === 'premium' ? 'Pro' : 'Miễn phí'}</H>
                <p style={{ color: '#535b71', fontSize: '0.8125rem', marginBottom: '1.25rem' }}>
                  {subscriptionTier === 'PRO' || subscriptionTier === 'premium'
                    ? 'Bạn đang dùng gói Pro — không giới hạn tính năng.'
                    : 'Miễn phí mãi mãi · Giới hạn số bài AI/ngày.'}
                </p>
              </div>
              {subscriptionTier !== 'PRO' && subscriptionTier !== 'premium' ? (
                <button
                  onClick={() => navigate('/pricing')}
                  style={{ width: '100%', padding: '0.875rem', borderRadius: '9999px', border: 'none', background: 'linear-gradient(135deg,#004be2,#809bff)', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,75,226,0.25)' }}
                >
                  Nâng cấp Pro
                </button>
              ) : (
                <div style={{ width: '100%', padding: '0.875rem', borderRadius: '9999px', background: 'rgba(0,75,226,0.06)', color: '#004be2', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, textAlign: 'center', fontSize: '0.9375rem' }}>
                  ✓ Đang dùng Pro
                </div>
              )}
            </div>

            {/* Daily goal */}
            <div style={{ background: '#eef0ff', borderRadius: '1.5rem', padding: '1.75rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className="material-symbols-outlined" style={{ color: '#006571', fontSize: '1.25rem' }}>bolt</span>
                <H style={{ fontWeight: 800, fontSize: '1rem' }}>Mục tiêu mỗi ngày</H>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '0.625rem' }}>
                  <H style={{ fontWeight: 900, fontSize: '2rem' }}>{dailyGoal}<span style={{ fontSize: '0.875rem', fontWeight: 400, color: '#535b71' }}> phút</span></H>
                  <span style={{ fontSize: '0.6875rem', fontWeight: 800, color: '#006571', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Cường độ cao</span>
                </div>
                <input
                  type="range"
                  min={15}
                  max={120}
                  value={dailyGoal}
                  onChange={e => { const v = Number(e.target.value); setDailyGoal(v); saveDailyGoalDebounced(v) }}
                  style={{ width: '100%', accentColor: '#004be2', cursor: 'pointer' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', fontWeight: 700, color: '#a5adc6', marginTop: '0.375rem' }}>
                  <span>15p</span><span>60p</span><span>120p</span>
                </div>
              </div>
              <p style={{ fontSize: '0.75rem', color: '#535b71', fontStyle: 'italic' }}>Đạt mục tiêu để duy trì streak!</p>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: goalsMutation.isPending ? '#535b71' : '#006571', display: 'flex', alignItems: 'center', gap: '0.25rem', minHeight: '1rem' }}>
                {goalsMutation.isPending ? (
                  <><span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>sync</span>Đang lưu...</>
                ) : goalSaved ? (
                  <><span className="material-symbols-outlined" style={{ fontSize: '0.875rem', fontVariationSettings: "'FILL' 1" }}>check_circle</span>Đã lưu tự động</>
                ) : null}
              </span>
            </div>
          </div>

          {/* Account settings */}
          <div style={{ background: 'white', borderRadius: '1.5rem', padding: '2rem', boxShadow: '0 8px 24px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.75rem' }}>
              <H style={{ fontWeight: 800, fontSize: '1.125rem' }}>Cài đặt tài khoản</H>
              <button style={{ background: 'none', border: 'none', color: '#004be2', fontWeight: 700, fontSize: '0.8125rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>history</span>
                Lịch sử
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Họ và tên</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Nguyễn Văn A"
                  style={{ width: '100%', padding: '0.875rem 1rem', borderRadius: '0.875rem', background: '#eef0ff', border: 'none', outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '16px', color: '#272e42', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Email</label>
                <input
                  type="text"
                  value={email}
                  readOnly
                  style={{ width: '100%', padding: '0.875rem 1rem', borderRadius: '0.875rem', background: '#f0f2fa', border: 'none', outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', color: '#a5adc6', boxSizing: 'border-box', cursor: 'default' }}
                />
              </div>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 800, color: '#535b71', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.5rem' }}>Mục tiêu / Động lực</label>
              <textarea
                value={motivation}
                onChange={e => setMotivation(e.target.value)}
                maxLength={2000}
                placeholder="Tôi đang học VSTEP để đạt B2 và nộp hồ sơ thạc sĩ..."
                rows={3}
                style={{ width: '100%', padding: '0.875rem 1rem', borderRadius: '0.875rem', background: '#eef0ff', border: 'none', outline: 'none', fontFamily: "'Manrope', sans-serif", fontSize: '0.9375rem', color: '#272e42', resize: 'none', boxSizing: 'border-box' }}
              />
            </div>

            {/* Notifications */}
            <div style={{ borderTop: '1px solid #eef0ff', paddingTop: '1.5rem' }}>
              <H style={{ display: 'block', fontWeight: 800, fontSize: '1rem', marginBottom: '1rem' }}>Thông báo</H>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  { icon: 'chat_bubble', label: 'Nhắc học hàng ngày', desc: 'Nhắc nhẹ để duy trì streak của bạn', key: 'reminder', val: notifReminder, set: setNotifReminder },
                  { icon: 'trending_up', label: 'Báo cáo tuần', desc: 'Tổng hợp chi tiết tiến độ học tập mỗi tuần', key: 'weekly', val: notifWeekly, set: setNotifWeekly },
                ].map(item => (
                  <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#eef0ff', borderRadius: '1rem', padding: '1rem 1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
                      <span className="material-symbols-outlined" style={{ color: '#004be2', fontSize: '1.25rem' }}>{item.icon}</span>
                      <div>
                        <p style={{ fontWeight: 700, fontSize: '0.875rem', marginBottom: '0.125rem' }}>{item.label}</p>
                        <p style={{ fontSize: '0.75rem', color: '#535b71' }}>{item.desc}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => item.set(!item.val)}
                      style={{ width: '2.75rem', height: '1.5rem', borderRadius: '9999px', border: 'none', background: item.val ? '#004be2' : '#d1dcff', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}
                    >
                      <div style={{ position: 'absolute', top: '0.125rem', left: item.val ? '1.25rem' : '0.125rem', width: '1.25rem', height: '1.25rem', background: 'white', borderRadius: '50%', transition: 'left 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.15)' }} />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '0.75rem', marginTop: '1.75rem' }}>
              {saveSuccess && (
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#006571', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>check_circle</span>
                  Đã lưu!
                </span>
              )}
              {saveError && (
                <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#d92d20', display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <span className="material-symbols-outlined" style={{ fontSize: '1rem', fontVariationSettings: "'FILL' 1" }}>error</span>
                  {saveError}
                </span>
              )}
              <button
                onClick={() => {
                  setSaveError(null)
                  if (userMe) {
                    setName((userMe as any).name ?? '')
                    setTargetLevel((userMe as any).targetLevel ?? '')
                    setDailyGoal((userMe as any).dailyStudyMinutes ?? 45)
                    setMotivation((userMe as any).motivation ?? '')
                    setNotifReminder((userMe as any).notifDailyReminder ?? true)
                    setNotifWeekly((userMe as any).notifWeeklyReport ?? true)
                    const ed = (userMe as any).examDate
                    setExamDate(ed ? ed.slice(0, 10) : '')
                  }
                }}
                style={{ padding: '0.75rem 1.5rem', borderRadius: '9999px', border: 'none', background: 'transparent', color: '#535b71', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: 'pointer' }}
              >
                Hủy
              </button>
              <button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                style={{ padding: '0.75rem 2.5rem', borderRadius: '9999px', border: 'none', background: 'linear-gradient(135deg,#004be2,#809bff)', color: 'white', fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, cursor: saveMutation.isPending ? 'wait' : 'pointer', boxShadow: '0 8px 20px rgba(0,75,226,0.25)', opacity: saveMutation.isPending ? 0.7 : 1 }}
              >
                {saveMutation.isPending ? 'Đang lưu...' : 'Lưu thay đổi'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}
